import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import formatDateWithTimezone from "@insite/client-framework/Common/Utilities/formatDateWithTimezone";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import DatePicker from "@insite/mobius/DatePicker";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Modal from "@insite/mobius/Modal";
import Select from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import ScrollToTop from "@NA/Components/ScrollToTop";
import {
    BannerActionParameter,
    BannerContentRow,
    deleteBanner,
    duplicateBanner,
    getBannerList,
    GetBannerListParameter,
} from "@NA/Services/ADIBannerListService";
import { ADIBannerSettings } from "@NA/Utils/ADIBannerSettings";
import ADIColors from "@NA/Utils/ADIColors";
import { formatEndDateTime } from "@NA/Utils/Utility";
import { ChangeEvent, FC, useContext, useEffect, useState } from "react";
import * as React from "react";
import { css } from "styled-components";

interface ADIBannerListStyles {
    loadingOverlay?: LoadingOverlayProps;
    groupItemTop?: InjectableCss;
    groupItemMiddle?: InjectableCss;
    groupItemBottom?: InjectableCss;
    datePickerWrapper?: InjectableCss;
    divider?: InjectableCss;
    listButton?: ButtonPresentationProps;
    containerBottom?: InjectableCss;
    titleText?: TypographyProps;
    resultText?: TypographyProps;
    bannerList?: DataTableProps;
    sortColumn?: InjectableCss;
    fixedCells?: InjectableCss;
    modalButtonGroup?: InjectableCss;
    modalButton?: InjectableCss;
}

const styles: ADIBannerListStyles = {
    loadingOverlay: {
        css: css`
            & > div:first-child[data-loading="true"] {
                position: fixed;
            }
        `,
    },
    groupItemTop: {
        css: css`
            padding: 13px 15px 5px 15px;
        `,
    },
    groupItemMiddle: {
        css: css`
            padding: 5px 15px;
        `,
    },
    groupItemBottom: {
        css: css`
            padding: 5px 15px 15px 15px;
        `,
    },
    datePickerWrapper: {
        css: css`
            width: 100%;
            .react-datetime-picker {
                padding: 5px 14px !important;
            }
            .react-datetime-picker:focus-within {
                padding: 4px 14px !important;
            }
        `,
    },
    divider: {
        css: css`
            border-top: 1px solid ${ADIColors.gray2};
            margin-top: 12px;
            width: 100%;
        `,
    },
    listButton: {
        css: css`
            width: 100%;
        `,
    },
    containerBottom: {
        css: css`
            margin-top: 50px;
        `,
    },
    titleText: {
        css: css`
            margin-bottom: 12px;
        `,
    },
    resultText: {
        css: css`
            display: block;
            text-align: center;
            padding: 30px;
        `,
    },
    bannerList: {
        css: css`
            table-layout: fixed;
            th {
                background-color: ${ADIColors.darkBlue1};
                border: 1px solid ${ADIColors.darkBlue1};
                color: ${ADIColors.white};
            }
            tbody {
                border: 1px solid ${ADIColors.gray2};
            }
        `,
    },
    sortColumn: {
        css: css`
            color: ${ADIColors.white};
            text-decoration: underline;
            span {
                color: ${ADIColors.white};
            }
        `,
    },
    fixedCells: {
        css: css`
            td {
                white-space: normal;
            }
        `,
    },
    modalButtonGroup: {
        css: css`
            margin-top: 20px;
        `,
    },
    modalButton: {
        css: css`
            margin-right: 12px;
        `,
    },
};

const ADIBannerList: FC = () => {
    const [bannerId, setBannerId] = useState("");
    const [bannerList, setBannerList] = useState<BannerContentRow[]>([]);
    const [bannerListParameter, setBannerListParameter] = useState<GetBannerListParameter>(
        {} as GetBannerListParameter,
    );

    const [isYesNoModalOpen, setIsYesNoModalOpen] = useState(false);
    const [yesNoModalType, setYesNoModalType] = useState("");

    const [loading, setLoading] = useState(false);
    const toasterContext = useContext(ToasterContext);

    const handleSearch = async () => {
        setLoading(true);
        const response = await getBannerList(bannerListParameter);
        if (response.successful) {
            setBannerList(response.result.colBannerContent);
        } else {
            toasterContext.addToast({
                messageType: "danger",
                body: translate("Unable to find banners, please try again!"),
            });
        }
        setLoading(false);
    };

    const handleClear = () => {
        setBannerList([]);
        setBannerListParameter({} as GetBannerListParameter);
    };

    const handleDuplicate = async () => {
        setIsYesNoModalOpen(false);
        setLoading(true);
        const response = await duplicateBanner({ parameter: bannerId } as BannerActionParameter);
        if (response !== "") {
            window.open(`/MyAccount/AddNewBanner?id=${response}`, "_blank");
            handleSearch();
        } else {
            toasterContext.addToast({
                messageType: "danger",
                body: translate("Unable to duplicate the banner, please try again!"),
            });
        }
        setLoading(false);
    };

    const handleDelete = async () => {
        setIsYesNoModalOpen(false);
        setLoading(true);
        const response = await deleteBanner({ parameter: bannerId } as BannerActionParameter);
        if (response) {
            toasterContext.addToast({
                messageType: "success",
                body: translate("Banner has been deleted!"),
            });
            handleSearch();
        } else {
            toasterContext.addToast({
                messageType: "danger",
                body: translate("Unable to delete the banner, please try again!"),
            });
            setLoading(false);
        }
    };

    useEffect(() => {
        if (typeof window !== undefined) {
            setTimeout(() => {
                window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                });
            }, 100);
        }
    }, []);

    return (
        <LoadingOverlay {...styles.loadingOverlay} loading={loading}>
            <GridContainer>
                <GridItem width={3}>
                    <TextField
                        label={translate("Description")}
                        value={bannerListParameter.description || ""}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setBannerListParameter({ ...bannerListParameter, description: e.target.value })
                        }
                    />
                </GridItem>
                <GridItem width={3}>
                    <TextField
                        label={translate("Customer #")}
                        value={bannerListParameter.customerNumber || ""}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setBannerListParameter({ ...bannerListParameter, customerNumber: e.target.value })
                        }
                    />
                </GridItem>
                <GridItem width={3}>
                    <TextField
                        label={translate("DC")}
                        value={bannerListParameter.dc || ""}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setBannerListParameter({ ...bannerListParameter, dc: e.target.value })
                        }
                    />
                </GridItem>
                <GridItem width={3}>
                    <TextField
                        label={translate("Rank")}
                        value={bannerListParameter.rank ?? ""}
                        type="number"
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setBannerListParameter({ ...bannerListParameter, rank: parseFloat(e.target.value) })
                        }
                    />
                </GridItem>
                <GridItem width={3}>
                    <Select
                        label={translate("Authentication")}
                        value={bannerListParameter.persona || ""}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                            setBannerListParameter({ ...bannerListParameter, persona: e.target.value })
                        }
                    >
                        <option key={0} value=""></option>
                        <option key={1} value="All">
                            {translate("All")}
                        </option>
                        <option key={2} value="Auth">
                            {translate("Auth Only")}
                        </option>
                        <option key={3} value="UnAuth">
                            {translate("UnAuth Only")}
                        </option>
                    </Select>
                </GridItem>
                <GridItem width={3}>
                    <Select
                        label={translate("Site")}
                        value={bannerListParameter.siteName || ""}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                            setBannerListParameter({ ...bannerListParameter, siteName: e.target.value })
                        }
                    >
                        <option key={0} value=""></option>
                        <option key={1} value={ADIBannerSettings.websiteCode.US}>
                            {translate("US")}
                        </option>
                        <option key={2} value={ADIBannerSettings.websiteCode.CAEN}>
                            {translate("CAEN")}
                        </option>
                        <option key={3} value={ADIBannerSettings.websiteCode.CAFR}>
                            {translate("CAFR")}
                        </option>
                        <option key={4} value={ADIBannerSettings.websiteCode.PR}>
                            {translate("PR")}
                        </option>
                    </Select>
                </GridItem>
                <GridItem width={3}>
                    <TextField
                        label={translate("Page URL")}
                        value={bannerListParameter.pageRelativeURL || ""}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setBannerListParameter({ ...bannerListParameter, pageRelativeURL: e.target.value })
                        }
                    />
                </GridItem>
                <GridItem width={3}>
                    <GridContainer>
                        <GridItem width={12} {...styles.groupItemTop}>
                            <StyledWrapper {...styles.datePickerWrapper}>
                                <DatePicker
                                format="MM/dd/y HH:mm"
                                    label={translate("From")}
                                    sizeVariant="small"
                                    disabled={bannerListParameter.neverExpire}
                                    cssOverrides={{
                                        formField: "width: 100%;" as any,
                                    }}
                                    selectedDay={
                                        bannerListParameter.startDate
                                            ? new Date(bannerListParameter.startDate)
                                            : undefined
                                    }
                                    onDayChange={date =>
                                        setBannerListParameter({
                                            ...bannerListParameter,
                                            startDate: date.selectedDay ? formatDateWithTimezone(date.selectedDay) : "",
                                        })
                                    }
                                />
                            </StyledWrapper>
                        </GridItem>
                        <GridItem width={12} {...styles.groupItemMiddle}>
                            <StyledWrapper {...styles.datePickerWrapper}>
                                <DatePicker
                                    label={translate("To")}
                                    format="MM/dd/y HH:mm"
                                    sizeVariant="small"
                                    disabled={bannerListParameter.neverExpire}
                                    cssOverrides={{
                                        formField: "width: 100%;" as any,
                                    }}
                                    selectedDay={
                                        bannerListParameter.endDate ? new Date(bannerListParameter.endDate) : undefined
                                    }
                                    onDayChange={date =>
                                        setBannerListParameter({
                                            ...bannerListParameter,
                                            endDate: date.selectedDay ? formatEndDateTime(date.selectedDay) : "",
                                        })
                                    }
                                />
                            </StyledWrapper>
                        </GridItem>
                        <GridItem width={12} {...styles.groupItemBottom}>
                            <Checkbox
                                checked={bannerListParameter.neverExpire}
                                onChange={(e, currentValue) =>
                                    setBannerListParameter({ ...bannerListParameter, neverExpire: currentValue })
                                }
                            >
                                {translate("Live")}
                            </Checkbox>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem width={12}>
                    <StyledWrapper {...styles.divider} />
                </GridItem>
                <GridItem width={3}>
                    <Link
                        href="/MyAccount/AddNewBanner"
                        style={{ width: "100%" }}
                        typographyProps={{
                            css: css`
                                width: 100%;
                            `,
                        }}
                    >
                        <Button {...styles.listButton} onClick={() => {}}>
                            {translate("New Banner")}
                        </Button>
                    </Link>
                </GridItem>
                <GridItem width={4} />
                <GridItem width={2}>
                    <Button buttonType="outline" {...styles.listButton} onClick={handleClear}>
                        <Typography color={ADIColors.blue5} style={{ fontSize: "16px", fontWeight: 700 }}>
                            {translate("Clear")}
                        </Typography>
                    </Button>
                </GridItem>
                <GridItem width={3}>
                    <Button {...styles.listButton} onClick={handleSearch}>
                        {translate("Search")}
                    </Button>
                </GridItem>
            </GridContainer>
            <StyledWrapper {...styles.containerBottom}>
                <Typography variant="h3" {...styles.titleText}>
                    {translate(
                        `Search Results ${bannerList && bannerList.length > 0 ? `(${bannerList.length} Results)` : ""}`,
                    )}
                </Typography>
                {bannerList && bannerList.length > 0 ? (
                    <>
                        <DataTable {...styles.bannerList}>
                            <DataTableHead>
                                <DataTableHeader tight style={{ width: "15%" }}>
                                    {translate("Description")}
                                </DataTableHeader>
                                <DataTableHeader tight style={{ width: "15%" }}>
                                    {translate("Banner Pages")}
                                </DataTableHeader>
                                <DataTableHeader
                                    onSortClick={() => {
                                        let sort = "";
                                        !bannerListParameter.sortOption ||
                                        bannerListParameter.sortOption === "StartDate DESC"
                                            ? (sort = "StartDate ASC")
                                            : (sort = "StartDate DESC");
                                        setBannerListParameter({
                                            ...bannerListParameter,
                                            sortOption: sort,
                                        });
                                        bannerListParameter.sortOption = sort;
                                        handleSearch();
                                    }}
                                    sorted={
                                        !bannerListParameter.sortOption ||
                                        bannerListParameter.sortOption === "StartDate DESC"
                                            ? "descending"
                                            : bannerListParameter.sortOption === "StartDate ASC"
                                            ? "ascending"
                                            : "none"
                                    }
                                    tight
                                    {...styles.sortColumn}
                                    style={{ width: "10%" }}
                                >
                                    {translate("Start Date")}
                                </DataTableHeader>
                                <DataTableHeader
                                    onSortClick={() => {
                                        let sort = "";
                                        bannerListParameter.sortOption === "EndDate DESC"
                                            ? (sort = "EndDate ASC")
                                            : (sort = "EndDate DESC");
                                        setBannerListParameter({
                                            ...bannerListParameter,
                                            sortOption: sort,
                                        });
                                        bannerListParameter.sortOption = sort;
                                        handleSearch();
                                    }}
                                    sorted={
                                        bannerListParameter.sortOption === "EndDate DESC"
                                            ? "descending"
                                            : bannerListParameter.sortOption === "EndDate ASC"
                                            ? "ascending"
                                            : "none"
                                    }
                                    tight
                                    {...styles.sortColumn}
                                    style={{ width: "10%" }}
                                >
                                    {translate("End Date")}
                                </DataTableHeader>
                                <DataTableHeader
                                    onSortClick={() => {
                                        let sort = "";
                                        bannerListParameter.sortOption === "Rank DESC"
                                            ? (sort = "Rank ASC")
                                            : (sort = "Rank DESC");
                                        setBannerListParameter({
                                            ...bannerListParameter,
                                            sortOption: sort,
                                        });
                                        bannerListParameter.sortOption = sort;
                                        handleSearch();
                                    }}
                                    sorted={
                                        bannerListParameter.sortOption === "Rank DESC"
                                            ? "descending"
                                            : bannerListParameter.sortOption === "Rank ASC"
                                            ? "ascending"
                                            : "none"
                                    }
                                    tight
                                    {...styles.sortColumn}
                                    style={{ width: "7.5%" }}
                                >
                                    {translate("Rank")}
                                </DataTableHeader>
                                <DataTableHeader tight style={{ width: "10%" }}>
                                    {translate("Authentication")}
                                </DataTableHeader>
                                <DataTableHeader tight style={{ width: "7.5%" }}>
                                    {translate("Website")}
                                </DataTableHeader>
                                <DataTableHeader
                                    onSortClick={() => {
                                        let sort = "";
                                        bannerListParameter.sortOption === "ModifiedOn DESC"
                                            ? (sort = "ModifiedOn ASC")
                                            : (sort = "ModifiedOn DESC");
                                        setBannerListParameter({
                                            ...bannerListParameter,
                                            sortOption: sort,
                                        });
                                        bannerListParameter.sortOption = sort;
                                        handleSearch();
                                    }}
                                    sorted={
                                        bannerListParameter.sortOption === "ModifiedOn DESC"
                                            ? "descending"
                                            : bannerListParameter.sortOption === "ModifiedOn ASC"
                                            ? "ascending"
                                            : "none"
                                    }
                                    tight
                                    {...styles.sortColumn}
                                    style={{ width: "10%" }}
                                >
                                    {translate("Modified On")}
                                </DataTableHeader>
                                <DataTableHeader tight style={{ width: "15%" }}>
                                    {translate("Actions")}
                                </DataTableHeader>
                            </DataTableHead>
                            <DataTableBody {...styles.fixedCells}>
                                {bannerList.map((row, index) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <DataTableRow key={index}>
                                        <DataTableCell>
                                            <Link href={`/MyAccount/AddNewBanner?id=${row.id}`} onClick={() => {}}>
                                                {row.description}
                                            </Link>
                                        </DataTableCell>
                                        <DataTableCell>{row.bannerPages}</DataTableCell>
                                        <DataTableCell>{new Date(row.startDate).toLocaleString()}</DataTableCell>
                                        <DataTableCell>{new Date(row.endDate).toLocaleString()}</DataTableCell>
                                        <DataTableCell>{row.rank}</DataTableCell>
                                        <DataTableCell>{row.persona}</DataTableCell>
                                        <DataTableCell>{row.websiteList}</DataTableCell>
                                        <DataTableCell>{new Date(row.modifiedOn).toLocaleString()}</DataTableCell>
                                        <DataTableCell>
                                            <GridContainer gap={8}>
                                                <GridItem width={6}>
                                                    <Link
                                                        onClick={() => {
                                                            setBannerId(row.id);
                                                            setYesNoModalType("duplicate");
                                                            setIsYesNoModalOpen(true);
                                                        }}
                                                    >
                                                        {translate("Duplicate")}
                                                    </Link>
                                                </GridItem>
                                                <GridItem width={6}>
                                                    <Link
                                                        onClick={() => {
                                                            setBannerId(row.id);
                                                            setYesNoModalType("delete");
                                                            setIsYesNoModalOpen(true);
                                                        }}
                                                    >
                                                        {translate("Delete")}
                                                    </Link>
                                                </GridItem>
                                            </GridContainer>
                                        </DataTableCell>
                                    </DataTableRow>
                                ))}
                            </DataTableBody>
                        </DataTable>
                    </>
                ) : (
                    <>
                        <StyledWrapper {...styles.divider} />
                        <Typography variant="h4" color={ADIColors.gray4} {...styles.resultText}>
                            {translate("No Search Results")}
                        </Typography>
                    </>
                )}
            </StyledWrapper>
            <Modal
                headline={translate("Question?")}
                isOpen={isYesNoModalOpen}
                handleClose={() => setIsYesNoModalOpen(false)}
            >
                <StyledWrapper>
                    <Typography>
                        {yesNoModalType === "duplicate"
                            ? translate("Are you sure you want to duplicate this banner?")
                            : translate("Are you sure you want to delete this banner?")}
                    </Typography>
                    <StyledWrapper {...styles.modalButtonGroup}>
                        <Button
                            {...styles.modalButton}
                            onClick={() => {
                                yesNoModalType === "duplicate" ? handleDuplicate() : handleDelete();
                            }}
                        >
                            {translate("Yes")}
                        </Button>
                        <Button onClick={() => setIsYesNoModalOpen(false)}>{translate("No")}</Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>
            <ScrollToTop />
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: ADIBannerList,
    definition: {
        group: "My Account",
        displayName: "ADI Banner List",
    } as WidgetDefinition,
};

export default widgetModule;
